export interface CalendarExportData {
  month: string
  year: number
  days: Array<{
    date: Date
    dayNumber: number
    isCurrentMonth: boolean
    isWeekend: boolean
    closureLabel?: string
    classes: Array<{
      title: string
      time: string
      price: string
      imageUrl?: string
    }>
  }>
}

// Printed calendars link back to the class schedule
const CALENDAR_QR_URL = 'https://saltymermaidart.com/classes'
const CALENDAR_QR_LABEL = 'saltymermaidart.com/classes'

// Load jsPDF from CDN
async function loadJsPDF(): Promise<any> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if ((window as any).jspdf?.jsPDF) {
      console.log('jsPDF already loaded')
      resolve((window as any).jspdf.jsPDF)
      return
    }

    console.log('Loading jsPDF from CDN...')
    const script = document.createElement('script')
    // Try unpkg CDN instead - more reliable for ESM packages
    script.src = 'https://unpkg.com/jspdf@2.5.1/dist/jspdf.umd.min.js'
    script.onload = () => {
      console.log('jsPDF CDN script loaded')
      if ((window as any).jspdf?.jsPDF) {
        console.log('jsPDF.jsPDF found on window')
        resolve((window as any).jspdf.jsPDF)
      } else {
        console.error('jsPDF loaded but jsPDF.jsPDF not found. Available:', Object.keys((window as any).jspdf || {}))
        reject(new Error('jsPDF loaded but constructor not found'))
      }
    }
    script.onerror = (error) => {
      console.error('Failed to load jsPDF from CDN:', error)
      reject(new Error('Failed to load jsPDF from CDN'))
    }
    document.head.appendChild(script)
  })
}

export async function exportCalendarToPDF(data: CalendarExportData): Promise<void> {
  let container: HTMLDivElement | null = null

  try {
    // Dynamically load jsPDF from CDN and html2canvas from npm
    console.log('Loading jsPDF and html2canvas...')
    const [jsPDF, { default: html2canvas }, { default: QRCode }] = await Promise.all([
      loadJsPDF(),
      import('html2canvas'),
      import('qrcode')
    ])
    console.log('jsPDF loaded:', typeof jsPDF)
    console.log('html2canvas loaded:', typeof html2canvas)

    const qrDataUrl = await QRCode.toDataURL(CALENDAR_QR_URL, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 300
    })

    // Create a hidden container for the calendar
    container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.left = '-9999px'
    container.style.top = '0'
    container.style.width = '1400px' // Fixed width for consistent rendering
    container.style.backgroundColor = 'white'
    container.style.padding = '20px'
    document.body.appendChild(container)

    // Build the calendar HTML
    container.innerHTML = `
      <div style="font-family: Arial, sans-serif; background: white; padding: 20px;">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 20px;">
          <h1 style="margin: 0; font-size: 28px; color: #333; font-weight: bold;">The Salty Mermaid - Art Classes - ${data.month} ${data.year}</h1>
        </div>

        <!-- Calendar Grid -->
        <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; border: 2px solid #333; background: #333;">
          <!-- Day Headers -->
          ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
            .map(day => `
              <div style="background: #d0d0d0; padding: 10px; text-align: center; font-weight: bold; font-size: 14px; color: #000;">
                ${day}
              </div>
            `).join('')}

          <!-- Calendar Days -->
          ${data.days.map(day => `
            <div style="
              background: ${day.closureLabel ? '#fee2e2' : day.isCurrentMonth ? (day.isWeekend ? '#fafafa' : 'white') : '#f5f5f5'};
              padding: 8px;
              min-height: 100px;
              position: relative;
            ">
              <!-- Day Number -->
              <div style="
                font-weight: bold;
                font-size: 14px;
                color: ${day.isCurrentMonth ? '#333' : '#999'};
                margin-bottom: 8px;
              ">
                ${day.dayNumber}
              </div>

              <!-- Closed Banner -->
              ${day.closureLabel ? `
                <div style="
                  background: #dc2626;
                  color: white;
                  border-radius: 4px;
                  padding: 6px 4px;
                  margin-bottom: 4px;
                  text-align: center;
                ">
                  <div style="font-size: 20px; font-weight: bold; letter-spacing: 1px; line-height: 1.2;">CLOSED</div>
                  <div style="font-size: 10px; font-weight: bold; line-height: 1.2;">${day.closureLabel}</div>
                </div>
              ` : ''}

              <!-- Classes -->
              <div style="display: flex; flex-direction: column; gap: 4px;">
                ${day.classes.map(classItem => `
                  <div style="
                    background: #e8e8e8;
                    border: 1px solid #666;
                    border-radius: 4px;
                    padding: 6px;
                    font-size: 10px;
                  ">
                    <div style="display: flex; gap: 6px; align-items: start;">
                      ${classItem.imageUrl ? `
                        <img
                          src="${classItem.imageUrl}"
                          style="
                            width: 30px;
                            height: 30px;
                            border-radius: 4px;
                            object-fit: cover;
                            flex-shrink: 0;
                          "
                          crossorigin="anonymous"
                        />
                      ` : ''}
                      <div style="flex: 1; min-width: 0;">
                        <div style="font-weight: bold; color: #1565c0; margin-bottom: 2px; line-height: 1.2; word-wrap: break-word;">
                          ${classItem.title}
                        </div>
                        <div style="color: #666; font-size: 9px; margin-bottom: 2px;">
                          ${classItem.time}
                        </div>
                        <div style="font-weight: bold; color: #059669; font-size: 9px;">
                          ${classItem.price}
                        </div>
                      </div>
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          `).join('')}
        </div>

        <!-- QR code and URL, centered below the calendar -->
        <div style="text-align: center; margin-top: 16px;">
          <img src="${qrDataUrl}" style="width: 150px; height: 150px; display: block; margin: 0 auto;" />
          <div style="margin-top: 4px; font-size: 20px; font-weight: bold; color: #000;">${CALENDAR_QR_LABEL}</div>
        </div>
      </div>
    `

    // Wait for images to load
    console.log('Waiting for images to load...')
    const images = container.querySelectorAll('img')
    await Promise.all(
      Array.from(images).map(img => {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve(null)
          } else {
            img.onload = () => resolve(null)
            img.onerror = () => resolve(null) // Continue even if image fails
          }
        })
      })
    )

    // Small delay to ensure rendering is complete
    await new Promise(resolve => setTimeout(resolve, 100))

    // Capture the calendar as an image
    console.log('Capturing calendar with html2canvas...')
    const canvas = await html2canvas(container, {
      scale: 2, // Higher quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false
    })
    console.log('Canvas created:', canvas.width, 'x', canvas.height)

    // Create PDF (landscape orientation)
    console.log('Creating PDF...')
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    })
    console.log('PDF instance created')

    // A4 landscape: 297mm x 210mm
    // With 0.5 inch (12.7mm) margins on left, right, and bottom
    const margin = 12.7
    const pageWidth = 297
    const pageHeight = 210

    // Calculate available space
    const availableWidth = pageWidth - (margin * 2) // Left + Right margins
    const availableHeight = pageHeight - margin // Only bottom margin (title provides top space)

    // Calculate image dimensions to fit within available space
    let imgWidth = availableWidth
    let imgHeight = (canvas.height * imgWidth) / canvas.width

    // If height exceeds available space, scale down based on height
    if (imgHeight > availableHeight) {
      imgHeight = availableHeight
      imgWidth = (canvas.width * imgHeight) / canvas.height
    }

    // Center the image horizontally if needed
    const xOffset = margin + (availableWidth - imgWidth) / 2
    const yOffset = 0 // Start from top (title provides natural spacing)

    // Add the image to the PDF
    console.log('Adding image to PDF...')
    const imgData = canvas.toDataURL('image/png')
    pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight)
    console.log('Image added to PDF')

    // Download the PDF
    const filename = `The-Salty-Mermaid-Calendar-${data.month}-${data.year}.pdf`
    console.log('Saving PDF as:', filename)
    pdf.save(filename)
    console.log('PDF save() called successfully')

  } catch (error: any) {
    console.error('Error generating PDF:', error)
    const errorMessage = error?.message || error?.toString() || 'Unknown error'
    alert('Failed to generate PDF: ' + errorMessage + '\n\nPlease check the browser console for details.')
  } finally {
    // Clean up
    if (container && container.parentNode) {
      document.body.removeChild(container)
    }
  }
}
